import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Drawings.css";
import ActionButton from "../../ActionButton";
import { illustrations } from "../../../data/illustrations";

const Drawings: React.FC = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Ilustrações vindas de birds.ts (via data/illustrations)
  const drawings = illustrations;

  // Preload images for smoother carousel experience
  const preloadImage = (src: string) => {
    const img = new Image();
    img.src = src;
  };

  // Preload visible and adjacent images when component mounts
  useEffect(() => {
    // Preload first few images that are likely to be visible
    const initialLoadCount = Math.min(5, drawings.length);
    for (let i = 0; i < initialLoadCount; i++) {
      preloadImage(drawings[i].src);
    }
  }, [drawings]);

  // Preload images when scrolling to ensure smooth experience
  const preloadAdjacentImages = (scrollLeft: number) => {
    if (!carouselRef.current) return;

    const cardWidth = 300; // Approximate width of each card
    const visibleStart = Math.floor(scrollLeft / cardWidth);
    const visibleEnd = Math.min(visibleStart + 5, drawings.length); // Preload 5 ahead

    for (let i = Math.max(0, visibleStart - 1); i < visibleEnd + 2; i++) {
      if (drawings[i]) {
        preloadImage(drawings[i].src);
      }
    }
  };

  const scroll = (direction: "left" | "right") => {
    if (carouselRef.current) {
      const scrollAmount = 300; // Width of one image plus gap
      const newPosition =
        direction === "left"
          ? Math.max(0, scrollPosition - scrollAmount)
          : Math.min(
              carouselRef.current.scrollWidth - carouselRef.current.clientWidth,
              scrollPosition + scrollAmount
            );

      carouselRef.current.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
      setScrollPosition(newPosition);

      // Preload images around new position
      preloadAdjacentImages(newPosition);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleScroll = () => {
    if (carouselRef.current) {
      const newScrollLeft = carouselRef.current.scrollLeft;
      setScrollPosition(newScrollLeft);

      // Preload images as user scrolls
      preloadAdjacentImages(newScrollLeft);
    }
  };

  return (
    <div className="drawings-container">
      <h1 className="drawings-title">Ilustrações</h1>
      <div className="drawings">
        {/* Overlay image */}
        <div className="drawings-bg-overlay"></div>
        <div className="drawings-carousel">
          <button
            className="carousel-arrow carousel-arrow-left"
            onClick={() => scroll("left")}
          >
            <span>‹</span>
          </button>

          <div className="carousel-container">
            <div
              ref={carouselRef}
              className={`carousel-track ${isDragging ? "dragging" : ""}`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onScroll={handleScroll}
            >
              {drawings.map((drawing) => (
                <div key={drawing.src} className="carousel-slide">
                  <Link
                    to={`/ilustracoes?bird=${drawing.bird.id}&img=${drawing.index}`}
                    style={{ textDecoration: "none" }}
                  >
                    <div className="drawing-card">
                      <img
                        src={drawing.src}
                        alt={drawing.name}
                        className="drawing-image"
                        draggable={false}
                        loading="lazy"
                        decoding="async"
                        onLoad={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.classList.add("loaded");
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.opacity = "0.5";
                          target.style.filter = "grayscale(100%)";
                          console.warn(
                            `Failed to load image: ${drawing.src}`
                          );
                        }}
                      />
                    </div>
                  </Link>
                </div>
              ))}
            </div>
            <div className="fade-left"></div>
            <div className="fade-right"></div>
          </div>

          <button
            className="carousel-arrow carousel-arrow-right"
            onClick={() => scroll("right")}
          >
            <span>›</span>
          </button>
        </div>

        <div className="drawings-button-row">
          <Link to="/ilustracoes" style={{ textDecoration: "none" }}>
            <ActionButton
              color="var(--dark-background-blue)"
              hoverColor="#061624ff"
              text="Ver Tudo"
            />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Drawings;
